<template>
  <div class="chat-widget">
    <!-- Floating button -->
    <button
      v-if="!isOpen"
      class="chat-fab"
      @click="openChat"
      title="Chat GenIA"
    >
      <b-icon-chat-dots-fill />
    </button>

    <!-- Chat panel -->
    <div v-if="isOpen" class="chat-panel" :class="{ 'chat-panel--expanded': isExpanded }">
      <!-- Header -->
      <div class="chat-header">
        <b-form-select
          v-model="selectedAgentId"
          :options="agentOptions"
          size="sm"
          class="agent-select"
          @change="onAgentChange"
        />
        <button class="chat-new-conv" @click="startNewConversation" title="Nova conversa"
          v-if="messages.length > 0">
          +
        </button>
        <button class="chat-expand" @click="toggleExpanded" :title="isExpanded ? 'Recolher' : 'Expandir'">
          <!-- Ícone expand/collapse simples; substituível por SVG depois -->
          {{ isExpanded ? '⤡' : '⤢' }}
        </button>
        <button class="chat-close" @click="closeChat" title="Fechar">
          &times;
        </button>
      </div>

      <!-- Messages area -->
      <div class="chat-messages" ref="messagesContainer">
        <ChatMessage
          v-for="(msg, idx) in messages"
          :key="idx"
          :message="msg"
        />
        <div v-if="docProcessing" class="chat-doc-processing">
          Processando documento...
        </div>
        <div v-if="isLoading && !docProcessing" class="chat-typing">
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
        </div>
      </div>

      <!-- Error banner -->
      <div v-if="error" class="chat-error">
        {{ error }}
        <button class="chat-error-dismiss" @click="error = null">&times;</button>
      </div>

      <!-- File preview -->
      <div v-if="selectedFiles.length > 0" class="chat-file-preview">
        <div v-for="(file, idx) in selectedFiles" :key="idx" class="file-chip">
          <span class="file-chip-name">{{ file.name }}</span>
          <span class="file-chip-size">({{ formatFileSize(file.size) }})</span>
          <button class="file-chip-remove" @click="removeFile(idx)">&times;</button>
        </div>
      </div>

      <!-- Input area -->
      <div class="chat-input">
        <input
          type="file"
          ref="fileInput"
          multiple
          :accept="fileAccept"
          @change="onFilesSelected"
          style="display:none"
        >
        <button
          class="chat-attach-btn"
          @click="$refs.fileInput.click()"
          :disabled="!selectedAgentId || isLoading"
          title="Anexar arquivo"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
        </button>
        <textarea
          v-model="inputText"
          :disabled="!selectedAgentId || isLoading"
          :placeholder="inputPlaceholder"
          rows="2"
          class="chat-textarea"
          @keydown.enter.exact.prevent="handleSend"
        />
        <b-button
          variant="primary"
          size="sm"
          :disabled="!canSend"
          @click="handleSend"
          class="chat-send-btn"
        >
          Enviar
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue';
import { fetchAgents, fetchInternalAgents, fetchHistory, sendMessage, cleanupConversation, ChatError } from '@/services/chat';
import { chatBus } from '@/events/chatBus';
import { shouldLoadInternalAgents, getChatFileAccept } from '@/utils/internalAgents';

export default {
  name: 'ChatWidget',
  components: { ChatMessage },

  data() {
    return {
      isOpen: false,
      agents: [],
      selectedAgentId: null,
      messages: [],
      inputText: '',
      isLoading: false,
      responseId: null,
      pendingResponseId: null,
      abortController: null,
      error: null,
      agentsLoaded: false,
      // #538 Correção 2: flag separada de `agentsLoaded` — impede recarregar
      // a lista interna (genia-intake) em loop, mesmo se `loadAgents()` for
      // chamado de novo pelo watcher de `isManagement`. Decisão de quando
      // vale a pena buscar é do predicado puro `shouldLoadInternalAgents`.
      internalAgentsLoaded: false,
      selectedFiles: [],
      conversationId: null,             // number|null — DB conversation ID for persistence
      conversationImageItems: [],       // { type, file_id }[] — reusable image items for follow-ups
      cleanupFileIds: [],               // string[] — ALL file IDs (docs + images) for cleanup
      conversationVectorStoreId: null,  // string|null — vector store from doc uploads
      docProcessing: false,             // true while document is being indexed
      // Modo expandido no desktop: chat fica maior pra leitura de tabelas/listas.
      // Mobile sempre fullscreen via @media, ignora esta flag. Persistido em
      // localStorage pra preservar preferencia entre sessoes.
      isExpanded: (() => {
        try { return localStorage.getItem('chat-expanded') === '1'; }
        catch (_) { return false; }
      })(),
    };
  },

  computed: {
    agentOptions() {
      const options = [{ value: null, text: 'Selecione uma GenIA...' }];
      for (const agent of this.agents) {
        options.push({ value: agent.id, text: agent.label });
      }
      return options;
    },

    selectedAgent() {
      if (!this.selectedAgentId) return null;
      return this.agents.find((a) => a.id === this.selectedAgentId) || null;
    },

    canSend() {
      return (
        this.selectedAgentId &&
        (this.inputText.trim().length > 0 || this.selectedFiles.length > 0) &&
        !this.isLoading
      );
    },

    inputPlaceholder() {
      if (!this.selectedAgentId) return 'Selecione uma GenIA para começar';
      if (this.isLoading) return 'Aguarde a resposta...';
      return 'Digite sua mensagem ou anexe arquivos...';
    },

    // #538 v2: agentes internos (genia-intake) só entram na lista pro Admin.
    isManagement() {
      return this.$store.getters.isManagement;
    },

    // #538 Correção 2: objeto de usuário cru do store (não o booleano
    // `isManagement`) — é o que `shouldLoadInternalAgents` precisa pra
    // distinguir "sessão ainda não restaurada" de "restaurada e não é
    // Admin" (ver client/src/utils/internalAgents.js).
    sessionUser() {
      return this.$store.getters.sessionUser;
    },

    // #564: o seletor de arquivo só oferece `.xlsx,.xls` quando a GenIA
    // selecionada é a de intake (predicado puro testado sem Vue, ver
    // client/src/utils/internalAgents.js).
    fileAccept() {
      return getChatFileAccept(this.selectedAgentId);
    },
  },

  watch: {
    // Risco de hidratação (Codex, #538 v2): App.vue monta o ChatWidget assim
    // que há token no localStorage (`v-if="isAuthenticated"`), mas
    // `SESSION_RESTORE` resolve DEPOIS, de forma assíncrona — `state.user`
    // só populado com o e-mail real quando a action termina. Se o Admin
    // abrir o chat (loadAgents) antes disso, `isManagement` ainda é false
    // e o agente interno não entra na lista daquela sessão. Este watcher
    // recarrega a lista assim que o usuário chega e é Admin.
    isManagement(nowIsManagement) {
      if (nowIsManagement && this.agentsLoaded) {
        this.loadAgents();
      }
    },
  },

  methods: {
    async openChat() {
      this.isOpen = true;
      if (!this.agentsLoaded) {
        await this.loadAgents();
      }
    },

    closeChat() {
      this.abortCurrentRequest();
      // Do NOT cleanup conversation files on close — conversation may be resumed on reopen
      this.isOpen = false;
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      try { localStorage.setItem('chat-expanded', this.isExpanded ? '1' : '0'); }
      catch (_) { /* localStorage unavailable — ignore, state still works in-session */ }
      // Scroll para o final apos resize pra manter ultima mensagem visivel
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },

    async loadAgents() {
      try {
        const publicAgents = await fetchAgents();
        let agents = publicAgents;

        // Mescla os agentes internos (genia-intake) SÓ para o Admin, e só
        // depois da sessão restaurada (#538 v2 + Correção 2 do stop-gate
        // rodada 2 — predicado puro e testado, ver
        // client/src/utils/internalAgents.js). Falha ao buscar internos não
        // deve travar a lista pública.
        if (shouldLoadInternalAgents(this.sessionUser, this.internalAgentsLoaded)) {
          try {
            const internalAgents = await fetchInternalAgents();
            agents = publicAgents.concat(internalAgents);
            this.internalAgentsLoaded = true;
          } catch (err) {
            console.error('ChatWidget loadInternalAgents error:', err);
          }
        }

        this.agents = agents;
        this.agentsLoaded = true;
      } catch (err) {
        this.error = 'Erro ao carregar agentes. Tente novamente.';
        console.error('ChatWidget loadAgents error:', err);
      }
    },

    async onAgentChange() {
      this.abortCurrentRequest();
      // Do NOT cleanup conversation files on agent change — conversation may be resumed later

      this.messages = [];
      this.responseId = null;
      this.pendingResponseId = null;
      this.conversationId = null;
      this.conversationVectorStoreId = null;
      this.conversationImageItems = [];  // Clear to prevent image leakage between agents
      this.cleanupFileIds = [];          // Clear local tracking (remote resources preserved)
      this.error = null;
      this.selectedFiles = [];

      if (!this.selectedAgentId) return;

      // Try to load previous conversation for this agent
      try {
        const history = await fetchHistory(this.selectedAgentId);
        if (history.conversationId && history.messages.length > 0) {
          this.conversationId = history.conversationId;
          this.responseId = history.responseId;
          this.conversationVectorStoreId = history.vectorStoreId;
          this.messages = history.messages;
          this.$nextTick(() => this.scrollToBottom());
          return;
        }
      } catch (err) {
        console.warn('ChatWidget: could not load history, starting fresh', err.message);
      }

      // No history — show welcome message
      if (this.selectedAgent && this.selectedAgent.welcome_message) {
        this.messages.push({
          role: 'assistant',
          content: this.selectedAgent.welcome_message,
          timestamp: new Date(),
        });
      }
    },

    async startNewConversation() {
      const hasUserHistory = this.messages.some((message) => message.role === 'user');
      if (hasUserHistory) {
        const result = await this.$swal.fire({
          title: 'Iniciar nova conversa?',
          text: 'O histórico atual ficará disponível apenas no monitor administrativo. Você não conseguirá voltar para esta conversa pelo chat.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, iniciar nova',
          cancelButtonText: 'Voltar',
          confirmButtonColor: '#0b6baf',
        });

        if (!result.isConfirmed) return;
      }

      this.cleanupConversationFiles();
      this.messages = [];
      this.responseId = null;
      this.pendingResponseId = null;
      this.conversationId = null;
      this.conversationVectorStoreId = null;
      this.conversationImageItems = [];
      this.error = null;

      if (this.selectedAgent && this.selectedAgent.welcome_message) {
        this.messages.push({
          role: 'assistant',
          content: this.selectedAgent.welcome_message,
          timestamp: new Date(),
        });
      }
    },

    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      // Append to selected (up to 5 total)
      const remaining = 5 - this.selectedFiles.length;
      this.selectedFiles.push(...files.slice(0, remaining));
      // Reset input so the same file can be re-selected
      event.target.value = '';
    },

    removeFile(idx) {
      this.selectedFiles.splice(idx, 1);
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    cleanupConversationFiles() {
      if (this.conversationVectorStoreId || this.cleanupFileIds.length > 0) {
        cleanupConversation(this.conversationVectorStoreId, this.cleanupFileIds);
        this.conversationVectorStoreId = null;
        this.cleanupFileIds = [];
        this.conversationImageItems = [];
      }
      this.conversationId = null;
    },

    async handleSend() {
      if (!this.canSend) return;

      const userInput = this.inputText.trim();
      this.inputText = '';
      this.error = null;

      // Build user message with file indicators
      const filesToSend = [...this.selectedFiles];
      let userContent = userInput;
      if (filesToSend.length > 0) {
        const fileLabels = filesToSend.map(f => `\uD83D\uDCCE ${f.name} (${this.formatFileSize(f.size)})`).join('\n');
        userContent = filesToSend.length > 0 && userInput
          ? `${fileLabels}\n\n${userInput}`
          : fileLabels + (userInput ? `\n\n${userInput}` : '');
      }

      this.messages.push({
        role: 'user',
        content: userContent,
        timestamp: new Date(),
      });

      this.selectedFiles = [];
      this.abortCurrentRequest();

      this.isLoading = true;
      this.abortController = new AbortController();

      let assistantIdx = -1;
      let hasReceivedContent = false;
      const itemsAddedThisTurn = []; // track files_attached items for abort cleanup

      this.scrollToBottom();

      try {
        const stream = sendMessage(
          this.selectedAgentId,
          userInput || '',
          this.responseId,
          this.abortController.signal,
          filesToSend.length > 0 ? filesToSend : null,
          this.conversationImageItems.length > 0 ? this.conversationImageItems : null,
          this.conversationVectorStoreId,
          this.conversationId
        );

        for await (const event of stream) {
          switch (event.type) {
            case 'doc_processing':
              this.docProcessing = true;
              break;

            case 'doc_ready':
              this.conversationVectorStoreId = event.vector_store_id;
              if (event.file_id) {
                this.cleanupFileIds.push(event.file_id);
                // Doc IDs go to cleanup only — NOT to conversationImageItems
              }
              this.docProcessing = false;
              break;

            case 'files_attached':
              // Image file items: reusable in follow-ups AND tracked for cleanup
              if (event.file_items && Array.isArray(event.file_items)) {
                for (const fi of event.file_items) {
                  if (fi.file_id) {
                    this.conversationImageItems.push({ type: fi.type, file_id: fi.file_id });
                    this.cleanupFileIds.push(fi.file_id);
                  }
                }
                itemsAddedThisTurn.push(...event.file_items);
              }
              break;

            case 'context_reset':
              // Backend detectou que o previous_response_id salvo expirou
              // na OpenAI e refez a chamada sem ele. Limpa o ponteiro local
              // para que a proxima mensagem desta sessao nao reenvie o id
              // morto.
              this.responseId = null;
              this.pendingResponseId = null;
              this.messages.push({
                role: 'assistant',
                content: event.message || 'O contexto anterior expirou, então reiniciei a conversa para continuar funcionando. Se quiser que eu considere os documentos antigos, anexe novamente os arquivos.',
                timestamp: new Date(),
                isSystemNotice: true,
              });
              break;

            case 'vector_store_reset':
              // Backend detectou que o vector store de documentos do usuario
              // (criado em uploads anteriores desta conversa) expirou na OpenAI
              // e refez a chamada sem ele. Limpa o ponteiro local para nao
              // reenviar o vs_id morto na proxima mensagem desta sessao.
              this.conversationVectorStoreId = null;
              this.messages.push({
                role: 'assistant',
                content: event.message || 'Os documentos anexados anteriormente expiraram. Para analisá-los de novo, anexe os arquivos novamente.',
                timestamp: new Date(),
                isSystemNotice: true,
              });
              break;

            case 'combined_reset':
              // Backend recuperou os DOIS na mesma tentativa: previous_response_id
              // e vector store do usuario. Limpa ambos os ponteiros e exibe UMA
              // mensagem só pro usuario, em vez de duas em sequencia.
              this.responseId = null;
              this.pendingResponseId = null;
              this.conversationVectorStoreId = null;
              this.messages.push({
                role: 'assistant',
                content: event.message || 'O contexto anterior e os documentos anexados expiraram. Reiniciei a conversa para continuar funcionando. Se precisar que eu considere os documentos antigos, anexe novamente os arquivos.',
                timestamp: new Date(),
                isSystemNotice: true,
              });
              break;

            case 'start':
              this.pendingResponseId = event.response_id;
              if (event.conversation_id) {
                this.conversationId = event.conversation_id;
              }
              this.messages.push({
                role: 'assistant',
                content: '',
                timestamp: new Date(),
              });
              assistantIdx = this.messages.length - 1;
              break;

            case 'chunk':
              hasReceivedContent = true;
              if (assistantIdx >= 0) {
                this.$set(
                  this.messages,
                  assistantIdx,
                  {
                    ...this.messages[assistantIdx],
                    content: this.messages[assistantIdx].content + event.content,
                  }
                );
                this.scrollToBottom();
              }
              break;

            case 'citations':
              // Attach citation filenames to the current assistant message
              if (assistantIdx >= 0 && event.files && event.files.length > 0) {
                this.$set(
                  this.messages,
                  assistantIdx,
                  {
                    ...this.messages[assistantIdx],
                    citations: event.files,
                  }
                );
              }
              break;

            case 'done':
              this.responseId = this.pendingResponseId;
              this.pendingResponseId = null;
              this.isLoading = false;
              if (!hasReceivedContent && assistantIdx >= 0) {
                this.messages.splice(assistantIdx, 1);
              }
              break;

            case 'token_warning':
              this.error = `Atenção: você já usou ${event.pct}% do limite mensal de tokens (${event.used.toLocaleString()} / ${event.monthlyLimit.toLocaleString()}).`;
              break;

            case 'intake_result':
              // genia-intake (#538, P1 "resultado não chega ao vivo"):
              // evento exclusivo do canal do intake — sucesso (nº + link do
              // card) ou falha do Trello, entregue na própria conversa.
              // Mudança aditiva: nenhum outro agente emite este evento.
              this.messages.push({
                role: 'assistant',
                content: event.message || '',
                timestamp: new Date(),
                isSystemNotice: true,
              });
              break;

            case 'error':
              this.error = event.message || 'Erro na resposta da IA.';
              this.docProcessing = false;
              this.pendingResponseId = null;
              this.isLoading = false;
              if (!hasReceivedContent && assistantIdx >= 0) {
                this.messages.splice(assistantIdx, 1);
              }
              break;
          }
        }
      } catch (err) {
        if (!hasReceivedContent && assistantIdx >= 0) {
          this.messages.splice(assistantIdx, 1);
        }

        if (err.name === 'AbortError') {
          // Backend deletes image files on abort; remove them from client state too
          if (itemsAddedThisTurn.length > 0) {
            const deletedIds = new Set(itemsAddedThisTurn.map(fi => fi.file_id));
            this.conversationImageItems = this.conversationImageItems.filter(
              fi => !deletedIds.has(fi.file_id)
            );
            this.cleanupFileIds = this.cleanupFileIds.filter(
              id => !deletedIds.has(id)
            );
          }
          this.docProcessing = false;
          this.pendingResponseId = null;
          this.isLoading = false;
          return;
        }

        if (err instanceof ChatError) {
          this.error = err.message;
        } else {
          this.error = 'Erro de conexão. Tente novamente.';
          console.error('ChatWidget sendMessage error:', err);
        }

        this.pendingResponseId = null;
        this.isLoading = false;
      }
    },

    abortCurrentRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.pendingResponseId = null;
      this.isLoading = false;
      this.error = null;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    /**
     * Abre o chat ja selecionando uma GenIA especifica.
     * Acionado pelo evento chat-open-agent (chatBus), por exemplo
     * a partir dos cards da Bancada.
     *
     * Valida o agentId contra this.agents apos openChat() porque a
     * Bancada usa cards hardcoded; uma GenIA desativada no configurador
     * nao retorna em /chat/agents e nao pode ser aberta por aqui.
     *
     * @param {string} agentId  id do backend (ex.: 'genia-cnis')
     */
    async openWithAgent(agentId) {
      if (!agentId) return;

      await this.openChat();

      const agentExists = this.agents.some((agent) => agent.id === agentId);
      if (!agentExists) {
        this.error = 'Esta GenIA não está disponível no momento.';
        return;
      }

      if (this.selectedAgentId !== agentId) {
        this.selectedAgentId = agentId;
        await this.onAgentChange();
      }
    },
  },

  mounted() {
    chatBus.$on('chat-open-agent', this.openWithAgent);
  },

  beforeDestroy() {
    chatBus.$off('chat-open-agent', this.openWithAgent);
  },
};
</script>

<style scoped>
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1100;
  font-family: inherit;
}

/* Floating action button */
.chat-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #ed7b02;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;
}

.chat-fab:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

/* Chat panel — 3 estados:
 *   normal desktop (default):  520 x 680
 *   expandido desktop (.chat-panel--expanded): min(960px, 92vw) x min(820px, 88vh)
 *   mobile (@media <= 640px):  fullscreen ignorando .chat-panel--expanded
 *
 * O salto de 380x500 (legado) pra 520x680 dá mais espaço pra tabelas/listas
 * sem virar fullscreen — quem quiser tela cheia clica no botão de expandir.
 */
.chat-panel {
  width: 520px;
  height: 680px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 100px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.2s ease, height 0.2s ease;
}

.chat-panel--expanded {
  width: min(960px, 92vw);
  height: min(820px, 88vh);
}

@media (max-width: 640px) {
  .chat-panel,
  .chat-panel--expanded {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    position: fixed;
    inset: 0;
  }
}

/* Header */
.chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background-color: #0b6baf;
  color: #fff;
}

.agent-select {
  flex: 1;
  font-size: 13px;
}

.chat-new-conv {
  background: none;
  border: 1px solid rgba(255,255,255,0.5);
  border-radius: 4px;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  padding: 0 6px;
  line-height: 1.2;
  opacity: 0.8;
  margin-right: 4px;
}

.chat-new-conv:hover {
  opacity: 1;
  background: rgba(255,255,255,0.15);
}

.chat-expand {
  background: none;
  border: 1px solid rgba(255,255,255,0.5);
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  padding: 0 6px;
  line-height: 1.4;
  opacity: 0.8;
  margin-right: 4px;
}

.chat-expand:hover {
  opacity: 1;
  background: rgba(255,255,255,0.15);
}

.chat-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  opacity: 0.8;
}

.chat-close:hover {
  opacity: 1;
}

/* Messages area */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 4px;
  background-color: #fafafa;
}

/* Document processing indicator */
.chat-doc-processing {
  padding: 8px 16px;
  font-size: 12px;
  color: #0b6baf;
  font-style: italic;
  animation: doc-pulse 1.5s ease-in-out infinite;
}

@keyframes doc-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Typing indicator */
.chat-typing {
  display: flex;
  gap: 4px;
  padding: 8px 16px;
}

.typing-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: typing-bounce 1.2s ease-in-out infinite;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-6px);
  }
}

/* Error banner */
.chat-error {
  padding: 6px 12px;
  background-color: #f8d7da;
  color: #721c24;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-error-dismiss {
  background: none;
  border: none;
  color: #721c24;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
}

/* File preview chips */
.chat-file-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 10px;
  border-top: 1px solid #e0e0e0;
  background: #f5f5f5;
  max-height: 60px;
  overflow-y: auto;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  color: #1565c0;
}

.file-chip-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-chip-size {
  color: #666;
  font-size: 10px;
}

.file-chip-remove {
  background: none;
  border: none;
  color: #c62828;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

/* Input area */
.chat-input {
  display: flex;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #e0e0e0;
  background: #fff;
  align-items: center;
}

.chat-attach-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.chat-attach-btn:hover:not(:disabled) {
  opacity: 1;
}

.chat-attach-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.chat-textarea {
  flex: 1;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.4;
}

.chat-textarea:focus {
  border-color: #0b6baf;
}

.chat-textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.chat-send-btn {
  height: 36px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .chat-panel {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }

  .chat-widget {
    bottom: 16px;
    right: 16px;
  }
}
</style>
